import { ABAC, OrganizationTypes } from '.';
import { ABACAction, ABACSubject } from './abac';
import { UserType } from './DynamoTypes';

export type EnforceCollaboratorMedicalProfileAccessOptions = {
    enforceCollaborator: true;
};

export type HasAccessResponse = {
    hasAccess: boolean;
};

export type EnforcePolicyMedicalProfileAccessOptions = {
    action: ABACAction;
    subjects: ABACSubject[];
};

export type GetDoesHaveMedicalProfileAccessArgs = {
    type: 'getDoesHaveMedicalProfileAccess';
    medicalProfileId: string;
    userId: string;
} & (EnforceCollaboratorMedicalProfileAccessOptions | EnforcePolicyMedicalProfileAccessOptions);

export type GetIsProfileOwnerArgs = {
    type: 'getIsProfileOwner';
    profileId: string;
    userId: string;
};

export type GetUserOrganizationAccessArgs = {
    type: 'getUserOrganizationAccess';
    userId: string;
    organizationId: string;
};

export type UserOrganizationAccess = {
    isBillingAdmin: boolean;
    role: OrganizationTypes.OrganizationUserRole;
    organizationUserId: string;
};

export type GetUserOrganizationAccessResponse = UserOrganizationAccess | null;

export type OrganizationGroupActionOption = 'view' | 'edit';

export type GetDoesHaveOrganizationGroupAccessArgs = {
    type: 'getDoesHaveOrganizationGroupAccess';
    userId: string;
    organizationGroupId: string;
    action: OrganizationGroupActionOption;
    organizationId: string;
};

export type GetDoesHaveOrganizationGroupAccessResponse = HasAccessResponse;

export type GetIsUserTypeArgs = {
    type: 'getIsUserType';
    userType: UserType;
    userId: string;
};

export type GetValidatedOrganizationClientRecordArgs = {
    type: 'getValidatedOrganizationClientRecord';
    organizationId: string;
    organizationClientRecordId: string;
};

export type GetValidatedOrganizationClientRecordResponse = {
    organizationClientRecord: OrganizationTypes.OrganizationClientRecord;
} | null;

export type GetUserIdFromSubArgs = {
    type: 'getUserIdFromSub';
    sub: string;
};

export type GetUserIdFromSubResponse = {
    userId: string | null;
};

export type GetEnforceMedicalProfileEnforcementContextArgs = {
    type: 'getEnforceMedicalProfileEnforcementContext';
    userId: string;
    medicalProfileId: string;
};

export type GetEnforceMedicalProfileEnforcementContextResponse = {
    medicalProfileEnforcementContext: ABAC.MedicalProfileEnforcementContext;
};

export type GetOrganizationMedicalProfileEnforcementContextArgs = {
    type: 'getOrganizationMedicalProfileEnforcementContext';
    organizationId: string;
    medicalProfileId: string;
};

/**
 * The enforcement context an organization holds on a medical profile through its own collaborator record, or null
 * when the organization is not (or no longer) a collaborator on the profile. Unlike the user-scoped context it is
 * never an owner context and does not consult the organization's agent groups: it answers what the organization as
 * a whole may see.
 */
export type GetOrganizationMedicalProfileEnforcementContextResponse = {
    medicalProfileEnforcementContext: ABAC.MedicalProfileEnforcementContext | null;
};

export type GetUserOrganizationMirrorTargetArgs = {
    type: 'getUserOrganizationMirrorTarget';
    userId: string;
    medicalProfileId: string;
};

/**
 * The organization client record that receives an organization-owned copy of a profile note the user authors.
 */
export type UserOrganizationMirrorTarget = {
    organizationId: string;
    organizationClientRecordId: string;
};

export type GetUserOrganizationMirrorTargetResponse = {
    /**
     * Null when the user is not acting for an organization on this profile: not an organization member, a personal
     * collaborator on the profile (the personal role takes precedence), the organization is not a collaborator on
     * the profile, or the organization has no active or provisioned-invited client record for it.
     */
    mirrorTarget: UserOrganizationMirrorTarget | null;
};
